import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { CartContext } from '../../context/CartContext';
import toast from 'react-hot-toast';
import './CushionCustomizer.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";
const FALLBACK_IMAGE = "https://via.placeholder.com/800x800?text=No+Image+Available"; 

const CushionCustomizer = ({ product }) => {
  const [teacherName, setTeacherName] = useState('Mrs. Eliza Reed');
  const [quantity, setQuantity] = useState(1);
  const { addToCart } = useContext(CartContext);
  const navigate = useNavigate();

  // ===============================
  // DYNAMIC IMAGE RESOLVER
  // ===============================
  const resolveImageUrl = (img) => {
    if (!img) return FALLBACK_IMAGE;

    if (img.startsWith("http://") || img.startsWith("https://") || img.startsWith("data:")) {
      return img;
    }

    let cleanPath = img;
    if (cleanPath.startsWith("/uploads/")) {
      cleanPath = cleanPath.replace("/uploads/", "");
    } else if (cleanPath.startsWith("uploads/")) {
      cleanPath = cleanPath.replace("uploads/", "");
    }

    const baseUrl = API_BASE_URL.endsWith("/") 
      ? API_BASE_URL.slice(0, -1) 
      : API_BASE_URL;

    return `${baseUrl}/uploads/${cleanPath}`;
  };

  const getProductImage = () => {
    if (Array.isArray(product?.images) && product.images.length > 1 && product.images[1]) {
      return resolveImageUrl(product.images[1]);
    }
    if (Array.isArray(product?.images) && product.images.length > 0 && product.images[0]) {
      return resolveImageUrl(product.images[0]);
    }
    if (product?.image) {
      return resolveImageUrl(product.image);
    }
    return FALLBACK_IMAGE;
  };

  const displayImage = getProductImage();

  // ===============================
  // HANDLERS
  // ===============================
  const handleNameChange = (e) => {
    setTeacherName(e.target.value);
  };

  const increaseQty = () => {
    if (product?.stock && quantity < product.stock) {
      setQuantity((prev) => prev + 1);
    } else if (!product?.stock) {
      setQuantity((prev) => prev + 1);
    }
  };

  const decreaseQty = () => {
    if (quantity > 1) setQuantity((prev) => prev - 1);
  };

  const buildCustomProduct = () => ({
    ...product,
    _id: product?._id || "64f1b2c3e4d5a67890123456", 
    name: product?.name || "Teacher's Day Special Cushion",
    price: product?.price || 499,
    image: displayImage, 
    customText: teacherName.trim(), 
    quantity: quantity
  });

  const handleAddToCart = () => {
    if (!teacherName.trim()) {
      toast.error("Please enter a name for the cushion.");
      return;
    }
    if (product?.stock <= 0) {
      toast.error("This product is out of stock.");
      return;
    }
    addToCart(buildCustomProduct());
  };

  const handleBuyNow = () => {
    if (!teacherName.trim()) {
      toast.error("Please enter a name for the cushion.");
      return;
    }
    if (product?.stock <= 0) {
      toast.error("This product is out of stock.");
      return;
    }
    addToCart(buildCustomProduct());
    navigate("/cart");
  };

  const getCategoryName = (category) => {
    if (!category) return "Personalized Gifts";
    if (typeof category === "object") return category.name || "Personalized Gifts";
    return category;
  };

  return (
    <div className="product-details-page">
      <div className="container">
        
        {/* Topbar */}
        <div className="product-topbar">
          <span className="product-category">
            Home / {getCategoryName(product?.category)}
          </span>
        </div>

        <div className="product-wrapper">
          
          {/* ===============================
              LEFT COLUMN: IMAGE GALLERY (SVG)
          =============================== */}
          <div className="product-left">
            <div className="main-image-card">
              <div className="main-image customizer-main-image">
                <svg viewBox="0 0 1200 800" className="preview-svg">
                  {/* Base Cushion Image */}
                  <image 
                    href={displayImage} 
                    x="0" 
                    y="0" 
                    width="1200" 
                    height="800" 
                    preserveAspectRatio="xMidYMid slice" 
                  />
                  {/* Ribbon Text Container */}
                  <foreignObject x="400" y="490" width="460" height="110">
                    <div xmlns="http://www.w3.org/1999/xhtml" className="svg-text-wrapper">
                      <p className="svg-custom-text">
                        {teacherName || 'YOUR NAME HERE'}
                      </p>
                    </div>
                  </foreignObject>
                </svg>
              </div>
            </div>
          </div>

          {/* ===============================
              RIGHT COLUMN: INFO & ACTIONS
          =============================== */}
          <div className="product-right">
            
            <div className="product-title-row">
              <div className="product-heading-block">
                <h1>{product?.name || "Personalized Teacher's Day Cushion"}</h1>
              </div>
            </div>

            {/* Price & Stock Row */}
            <div className="price-stock-row">
              <div className="price-section">
                <p className="product-price">₹{product?.price || 499}</p>
                <p className="price-note">Inclusive of all taxes</p>
              </div>
              
              <div className={`stock-badge ${(product?.stock ?? 10) > 0 ? "in-stock" : "out-stock"}`}>
                {(product?.stock ?? 10) > 0 ? `In Stock (${product?.stock || 10})` : "Out Of Stock"}
              </div>
            </div>

            <p className="product-description">
              {product?.description || "Show your appreciation with this beautifully personalized cushion. Add your teacher's name to make it a memorable gift they'll cherish forever."}
            </p>

            <hr className="divider" />

            {/* 🎯 CUSTOMIZATION PANEL */}
            <div className="customization-panel">
              <label htmlFor="teacherName">Teacher's Name on Cushion</label>
              <div className="input-wrapper">
                <input
                  type="text"
                  id="teacherName"
                  value={teacherName}
                  onChange={handleNameChange}
                  maxLength={25}
                  placeholder="e.g. Mrs. Eliza Reed"
                />
                <span className="char-count">{teacherName.length}/25</span>
              </div>
            </div>

            {/* Purchase Panel (Qty + Buttons) */}
            <div className="purchase-panel">
              <div className="quantity-wrapper">
                <h4>Quantity</h4>
                <div className="quantity-box">
                  <button onClick={decreaseQty}>-</button>
                  <span>{quantity}</span>
                  <button onClick={increaseQty}>+</button>
                </div>
              </div>

              <div className="product-buttons">
                <button className="cart-btn" onClick={handleAddToCart} disabled={product?.stock <= 0}>
                  Add Custom Design
                </button>
                <button className="buy-btn" onClick={handleBuyNow} disabled={product?.stock <= 0}>
                  Buy Now
                </button>
              </div>
            </div>

            {/* Features List */}
            <div className="product-features">
              <h3>Why you'll like it</h3>
              <ul>
                <li>Custom printed with high-quality, fade-resistant ink.</li>
                <li>Premium ultra-soft fabric for ultimate comfort.</li>
                <li>Fast delivery with secure packaging support.</li>
                <li>Safe checkout with trusted payment options.</li>
              </ul>
            </div>
            
            <p className="product-extra-note">
              <i className="fa-solid fa-shield-halved" style={{marginRight: "8px"}}></i>
              Secure payments, quick support, and order tracking after purchase.
            </p>
            
          </div>
        </div>
      </div>
    </div>
  );
};

export default CushionCustomizer;